import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import NotificationBell from './NotificationBell/NotificationBell';
import FamilySwitcher from './FamilySwitcher';
import { isSystemAdmin } from '../utils/systemPermissions';
import { subscribeToUnreadCount } from '../services/messagingService';
import { Badge } from '@mui/material';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DarkMode,
  LightMode,
  Home as HomeIcon,
  Groups as FamilyIcon,
  PhotoLibrary as PhotoIcon,
  Description as DocumentIcon,
  Event as EventIcon,
  AccountTree as TreeIcon,
  Person as PersonIcon,
  Contacts as ContactsIcon,
  AdminPanelSettings as AdminIcon,
  Security as SystemAdminIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  HelpOutline as HelpIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Language as LanguageIcon,
  Info as InfoIcon,
  School as TutorialsIcon,
  SmartToy as AIAssistantIcon,
  Policy as PrivacyIcon,
  Gavel as TermsIcon,
  Cookie as CookieIcon,
  ChevronRight as ChevronRightIcon,
  Chat as ChatIcon,
  CreditCard as BillingIcon,
} from '@mui/icons-material';
import logger from '../utils/logger';

// Top-level routes where the back arrow should NOT appear
const TOP_LEVEL_PATHS = new Set([
  '/', '/home', '/family-board', '/messages', '/family-tree', '/family-directory',
  '/events', '/photos', '/documents', '/profile', '/contacts', '/help-center',
  '/admin', '/system-admin/dashboard', '/settings', '/login',
  '/emergency', '/health', '/announcements', '/discussions', '/goals', '/recipes',
  '/birthday-calendar', '/fun-social', '/recommendations',
  '/about', '/privacy', '/terms', '/cookies', '/tutorials',
  '/subscription',
]);

const Navigation = ({ onSignOut }) => {
  const [userGroups, setUserGroups] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const { currentUser } = useAuth();
  const { isDark, toggleTheme } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Show a back arrow whenever the user is on a non-top-level route
  const showBack = currentUser && !TOP_LEVEL_PATHS.has(location.pathname);

  // Menu anchor states
  const [familyMenuAnchor, setFamilyMenuAnchor] = useState(null);
  const [mediaMenuAnchor, setMediaMenuAnchor] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);

  // Avatar submenu states
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [helpMenuAnchor, setHelpMenuAnchor] = useState(null);
  const [learnMoreMenuAnchor, setLearnMoreMenuAnchor] = useState(null);

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileFamilyOpen, setMobileFamilyOpen] = useState(false);
  const [mobileMediaOpen, setMobileMediaOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser?.uid) {
        setUserProfile(null);
        return;
      }
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } catch (error) {
        logger.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [currentUser?.uid]);

  // Fetch user groups
  useEffect(() => {
    const fetchUserGroups = async () => {
      if (!currentUser?.uid || !currentUser?.familyCode) {
        setUserGroups([]);
        return;
      }
      try {
        const memberRef = doc(db, 'families', currentUser.familyCode, 'members', currentUser.uid);
        const memberDoc = await getDoc(memberRef);
        if (memberDoc.exists()) {
          setUserGroups(memberDoc.data().groups || []);
        }
      } catch (error) {
        logger.error('Error fetching user groups:', error);
      }
    };
    fetchUserGroups();
  }, [currentUser?.uid, currentUser?.familyCode]);

  // Unread message count for badge
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    if (!currentUser?.uid || !currentUser?.familyCode) return;
    const unsubscribe = subscribeToUnreadCount(currentUser.familyCode, currentUser.uid, setUnreadMessages);
    return () => unsubscribe();
  }, [currentUser?.uid, currentUser?.familyCode]);

  // Permission checks
  const isUserSystemAdmin = isSystemAdmin(userProfile);
  const isAdmin = userGroups.includes('ADMIN') || userProfile?.isAdmin || userProfile?.isSuperAdmin;

  // Check if a path is active
  const isActive = (path) => location.pathname === path;
  const isGroupActive = (paths) => paths.some(p => location.pathname.startsWith(p));

  // Menu handlers
  const handleMenuOpen = (setter) => (event) => setter(event.currentTarget);
  const handleMenuClose = (setter) => () => setter(null);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return 'U';
  };

  // Navigation groups
  const familyItems = [
    { text: 'Family Board', path: '/family-board', icon: <DashboardIcon /> },
    { text: 'Messages', path: '/messages', icon: <Badge badgeContent={unreadMessages} color="error" max={99}><ChatIcon /></Badge> },
    { text: 'Family Tree', path: '/family-tree', icon: <TreeIcon /> },
    { text: 'Family Directory', path: '/family-directory', icon: <BusinessIcon /> },
    { text: 'Events', path: '/events', icon: <EventIcon /> },
  ];

  const mediaItems = [
    { text: 'Photos', path: '/photos', icon: <PhotoIcon /> },
    { text: 'Documents', path: '/documents', icon: <DocumentIcon /> },
  ];

  const profileItems = [
    { text: 'Profile', path: '/profile', icon: <PersonIcon /> },
    { text: 'Contacts', path: '/contacts', icon: <ContactsIcon /> },
    { text: 'Billing & Subscription', path: '/subscription', icon: <BillingIcon /> },
    { text: 'Help Center', path: '/help-center', icon: <HelpIcon /> },
  ];

  const adminItems = [
    ...(isAdmin ? [{ text: 'Family Admin', path: '/admin', icon: <AdminIcon /> }] : []),
    ...(isUserSystemAdmin ? [{ text: 'System Admin', path: '/system-admin/dashboard', icon: <SystemAdminIcon /> }] : []),
  ];

  // Desktop Navigation
  const renderDesktopNav = () => (
    <>
      {/* Home - standalone */}
      <Button
        component={Link}
        to="/"
        startIcon={<HomeIcon />}
        sx={{
          color: isActive('/') ? 'primary.main' : 'text.primary',
          fontWeight: isActive('/') ? 700 : 500,
          textTransform: 'none',
          mx: 0.5
        }}
      >
        Home
      </Button>

      {/* Family dropdown */}
      <Button
        onClick={handleMenuOpen(setFamilyMenuAnchor)}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: isGroupActive(['/family-board', '/family-tree', '/family-directory', '/events']) ? 'primary.main' : 'text.primary',
          fontWeight: isGroupActive(['/family-board', '/family-tree', '/family-directory', '/events']) ? 700 : 500,
          textTransform: 'none',
          mx: 0.5
        }}
      >
        Family
      </Button>
      <Menu
        anchorEl={familyMenuAnchor}
        open={Boolean(familyMenuAnchor)}
        onClose={handleMenuClose(setFamilyMenuAnchor)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {familyItems.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMenuClose(setFamilyMenuAnchor)}
            selected={isActive(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Media dropdown */}
      <Button
        onClick={handleMenuOpen(setMediaMenuAnchor)}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: isGroupActive(['/photos', '/documents']) ? 'primary.main' : 'text.primary',
          fontWeight: isGroupActive(['/photos', '/documents']) ? 700 : 500,
          textTransform: 'none',
          mx: 0.5
        }}
      >
        Media
      </Button>
      <Menu
        anchorEl={mediaMenuAnchor}
        open={Boolean(mediaMenuAnchor)}
        onClose={handleMenuClose(setMediaMenuAnchor)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {mediaItems.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMenuClose(setMediaMenuAnchor)}
            selected={isActive(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Profile dropdown */}
      <Button
        onClick={handleMenuOpen(setProfileMenuAnchor)}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: isGroupActive(['/profile', '/contacts']) ? 'primary.main' : 'text.primary',
          fontWeight: isGroupActive(['/profile', '/contacts']) ? 700 : 500,
          textTransform: 'none',
          mx: 0.5
        }}
      >
        Profile
      </Button>
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleMenuClose(setProfileMenuAnchor)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {profileItems.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMenuClose(setProfileMenuAnchor)}
            selected={isActive(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Admin dropdown - only show if user has admin access */}
      {adminItems.length > 0 && (
        <>
          <Button
            onClick={handleMenuOpen(setAdminMenuAnchor)}
            endIcon={<ExpandMoreIcon />}
            sx={{
              color: isGroupActive(['/admin', '/system-admin']) ? 'primary.main' : 'text.primary',
              fontWeight: isGroupActive(['/admin', '/system-admin']) ? 700 : 500,
              textTransform: 'none',
              mx: 0.5
            }}
          >
            Admin
          </Button>
          <Menu
            anchorEl={adminMenuAnchor}
            open={Boolean(adminMenuAnchor)}
            onClose={handleMenuClose(setAdminMenuAnchor)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {adminItems.map((item) => (
              <MenuItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={handleMenuClose(setAdminMenuAnchor)}
                selected={isActive(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </>
  );

  // Mobile Navigation Drawer
  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      PaperProps={{ sx: { width: 280 } }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="primary" fontWeight={700}>
          FamilyForLife
        </Typography>
      </Box>
      <List>
        {/* Home */}
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/"
            selected={isActive('/')}
            onClick={() => setMobileDrawerOpen(false)}
          >
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>

        {/* Family */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setMobileFamilyOpen(!mobileFamilyOpen)}>
            <ListItemIcon><FamilyIcon /></ListItemIcon>
            <ListItemText primary="Family" />
            {mobileFamilyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={mobileFamilyOpen} timeout="auto">
          <List disablePadding>
            {familyItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                onClick={() => setMobileDrawerOpen(false)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Media */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setMobileMediaOpen(!mobileMediaOpen)}>
            <ListItemIcon><PhotoIcon /></ListItemIcon>
            <ListItemText primary="Media" />
            {mobileMediaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={mobileMediaOpen} timeout="auto">
          <List disablePadding>
            {mediaItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                onClick={() => setMobileDrawerOpen(false)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Profile */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setMobileProfileOpen(!mobileProfileOpen)}>
            <ListItemIcon><PersonIcon /></ListItemIcon>
            <ListItemText primary="Profile" />
            {mobileProfileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={mobileProfileOpen} timeout="auto">
          <List disablePadding>
            {profileItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                onClick={() => setMobileDrawerOpen(false)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Admin */}
        {adminItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setMobileAdminOpen(!mobileAdminOpen)}>
                <ListItemIcon><AdminIcon /></ListItemIcon>
                <ListItemText primary="Admin" />
                {mobileAdminOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={mobileAdminOpen} timeout="auto">
              <List disablePadding>
                {adminItems.map((item) => (
                  <ListItemButton
                    key={item.path}
                    component={Link}
                    to={item.path}
                    selected={isActive(item.path)}
                    onClick={() => setMobileDrawerOpen(false)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Sign Out */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => { setMobileDrawerOpen(false); onSignOut(); }}>
            <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
            <ListItemText primary="Sign Out" sx={{ color: 'error.main' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );

  if (!currentUser) {
    return (
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" color="primary" fontWeight={700} sx={{ flexGrow: 1 }}>
            FamilyForLife
          </Typography>
          <Button component={Link} to="/login" color="primary" variant="contained">
            Login
          </Button>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          {/* Mobile menu button — 48px touch target */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 1, minWidth: 48, minHeight: 48 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Back arrow on mobile — between hamburger and flex spacer */}
          {showBack && isMobile && (
            <Tooltip title="Go back">
              <IconButton
                color="inherit"
                onClick={() => navigate(-1)}
                size="small"
                aria-label="Go back"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Logo/Brand - hidden on mobile */}
          {!isMobile && (
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                textDecoration: 'none',
                mr: 3
              }}
            >
              FamilyForLife
            </Typography>
          )}

          {/* Back arrow on desktop — between logo and nav buttons */}
          {showBack && !isMobile && (
            <Tooltip title="Go back">
              <IconButton
                color="inherit"
                onClick={() => navigate(-1)}
                size="small"
                sx={{ mr: 1 }}
                aria-label="Go back"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              {renderDesktopNav()}
            </Box>
          )}

          {/* Spacer for mobile */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {/* Right side icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Family Switcher - shows when user belongs to multiple families */}
            <FamilySwitcher onSwitch={() => window.location.reload()} />

            {/* Theme toggle */}
            <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'}>
              <IconButton onClick={toggleTheme} color="inherit">
                {isDark ? <LightMode sx={{ color: '#ffd54f' }} /> : <DarkMode />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <NotificationBell />

            {/* Avatar with menu */}
            <Tooltip title="Account">
              <IconButton onClick={handleMenuOpen(setAvatarMenuAnchor)} sx={{ ml: 0.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    fontSize: '0.875rem'
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={avatarMenuAnchor}
              open={Boolean(avatarMenuAnchor)}
              onClose={() => {
                setAvatarMenuAnchor(null);
                setLanguageMenuAnchor(null);
                setHelpMenuAnchor(null);
                setLearnMoreMenuAnchor(null);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 220 } }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {userProfile?.displayName || currentUser?.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentUser?.email}
                </Typography>
              </Box>
              <Divider />

              {/* Settings */}
              <MenuItem
                component={Link}
                to="/settings"
                onClick={() => setAvatarMenuAnchor(null)}
              >
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>

              {/* Language - with submenu */}
              <MenuItem
                onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
              >
                <ListItemIcon><LanguageIcon /></ListItemIcon>
                <ListItemText>Language</ListItemText>
                <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>

              {/* Get Help - with submenu */}
              <MenuItem
                onClick={(e) => setHelpMenuAnchor(e.currentTarget)}
              >
                <ListItemIcon><HelpIcon /></ListItemIcon>
                <ListItemText>Get Help</ListItemText>
                <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>

              {/* Learn More - with submenu */}
              <MenuItem
                onClick={(e) => setLearnMoreMenuAnchor(e.currentTarget)}
              >
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText>Learn More</ListItemText>
                <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>

              {/* Billing & Subscription */}
              <MenuItem
                component={Link}
                to="/subscription"
                onClick={() => setAvatarMenuAnchor(null)}
              >
                <ListItemIcon><BillingIcon /></ListItemIcon>
                <ListItemText>Billing &amp; Subscription</ListItemText>
              </MenuItem>

              <Divider />

              {/* Sign Out */}
              <MenuItem onClick={() => { setAvatarMenuAnchor(null); onSignOut(); }}>
                <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>Sign Out</ListItemText>
              </MenuItem>
            </Menu>

            {/* Language Submenu */}
            <Menu
              anchorEl={languageMenuAnchor}
              open={Boolean(languageMenuAnchor)}
              onClose={() => setLanguageMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem onClick={() => { setLanguageMenuAnchor(null); setAvatarMenuAnchor(null); }}>
                <ListItemText>English (Default)</ListItemText>
              </MenuItem>
              <MenuItem disabled>
                <ListItemText secondary="More languages coming soon" />
              </MenuItem>
            </Menu>

            {/* Get Help Submenu */}
            <Menu
              anchorEl={helpMenuAnchor}
              open={Boolean(helpMenuAnchor)}
              onClose={() => setHelpMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                component={Link}
                to="/help-center"
                onClick={() => { setHelpMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><HelpIcon /></ListItemIcon>
                <ListItemText>Help Center</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/help-center"
                onClick={() => { setHelpMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><AIAssistantIcon /></ListItemIcon>
                <ListItemText>AI Assistant</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/tutorials"
                onClick={() => { setHelpMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><TutorialsIcon /></ListItemIcon>
                <ListItemText>Tutorials</ListItemText>
              </MenuItem>
            </Menu>

            {/* Learn More Submenu */}
            <Menu
              anchorEl={learnMoreMenuAnchor}
              open={Boolean(learnMoreMenuAnchor)}
              onClose={() => setLearnMoreMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                component={Link}
                to="/privacy"
                onClick={() => { setLearnMoreMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><PrivacyIcon /></ListItemIcon>
                <ListItemText>Privacy Policy</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/terms"
                onClick={() => { setLearnMoreMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><TermsIcon /></ListItemIcon>
                <ListItemText>Terms of Service</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/cookies"
                onClick={() => { setLearnMoreMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><CookieIcon /></ListItemIcon>
                <ListItemText>Cookie Policy</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/about"
                onClick={() => { setLearnMoreMenuAnchor(null); setAvatarMenuAnchor(null); }}
              >
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText>About FamilyForLife</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      {isMobile && renderMobileDrawer()}
    </>
  );
};

export default Navigation;
